"use client";

// Harga TERKINI bundel di keranjang (mig 8040) untuk pembeli yang login —
// dipakai /keranjang (kartu bundel, total, gerbang checkout, pratinjau voucher
// K5) dan /keranjang/voucher (kelayakan & hemat tiap tiket).
//
// Resolver yang SAMA dengan checkout (`resolveBundlesPricing`): definisi
// bundel dibaca ulang, harga terpisah tiap komponen lewat tangga `pricing.ts`,
// lalu `quoteBundle`. Resolver itu melempar pada bundel PERTAMA yang tak
// tersedia; supaya satu bundel rusak tak menyembunyikan harga bundel lain,
// kegagalan panggilan gabungan diulang per bundel dan galatnya ditempel ke
// bundel yang bersangkutan (kartu memajang sebabnya, checkout ditahan sampai
// bundelnya dihapus).
//
// Idiom `CartVoucherSection`: kunci efek berupa STRING (isi bundel + qty),
// dijeda 300 ms supaya stepper jumlah bundel tak menembak resolver tiap
// ketukan, dan hasil hanya sah untuk tanda tangan yang melahirkannya.

import { useCallback, useEffect, useMemo, useState } from "react";

import type { CartBundle } from "@/lib/bundleCart";
import {
  bundleCartSignature,
  bundleLiveErrorText,
  rekeyBundleEntry,
  type BundleLiveState
} from "@/lib/bundleCheckout";
import { resolveBundlesPricing, type ResolvedBundle } from "@/lib/supabase/data";

export type BundleCartPricing = {
  /** Tanda tangan isi bundel yang sedang dilihat ("" = tanpa bundel). */
  signature: string;
  /**
   * Harga untuk isi bundel SAAT INI sudah dijawab (berhasil atau gagal per
   * bundel). `reload()` mengembalikannya ke false sampai jawaban baru tiba —
   * angka yang dikonfirmasi checkout (§6.4) tak pernah jawaban sebelum muat ulang.
   */
  ready: boolean;
  /** Keadaan per `CartBundle.key` untuk isi SAAT INI; null = belum siap / tanpa pembeli login. */
  byKey: ReadonlyMap<string, BundleLiveState<ResolvedBundle>> | null;
  /**
   * Jawaban TERAKHIR untuk pembeli & level ini, walau isi bundel sudah berubah
   * sejak itu — untuk TAMPILAN kartu saja. Harga per 1 bundel bergantung pada
   * kunci bundel (bundel + varian pilihan), bukan jumlahnya, jadi kartu tak
   * berkedip ke harga potret tiap stepper jumlah ditekan. Angka yang mengikat
   * (voucher, checkout) tetap memakai `byKey` / `entries`.
   */
  lastByKey: ReadonlyMap<string, BundleLiveState<ResolvedBundle>> | null;
  /** Bundel yang harganya siap, urut keranjang; null = belum siap. */
  entries: ResolvedBundle[] | null;
  /** Ada bundel yang tak tersedia / gagal dihitung → checkout ditahan. */
  hasError: boolean;
  reload: () => void;
};

type Answer = {
  sig: string;
  owner: string;
  /** Generasi `reload()` yang melahirkan jawaban ini. */
  tick: number;
  byKey: Map<string, BundleLiveState<ResolvedBundle>>;
};

async function resolveLive(
  bundles: readonly CartBundle[],
  customerId: string
): Promise<Map<string, BundleLiveState<ResolvedBundle>>> {
  try {
    const all = await resolveBundlesPricing(bundles, customerId);
    return new Map(all.map((e) => [e.cartKey, { status: "ok", entry: e }] as const));
  } catch {
    const settled = await Promise.allSettled(bundles.map((b) => resolveBundlesPricing([b], customerId)));
    const out = new Map<string, BundleLiveState<ResolvedBundle>>();
    settled.forEach((r, i) => {
      const b = bundles[i];
      if (r.status === "fulfilled" && r.value[0]) {
        out.set(b.key, { status: "ok", entry: rekeyBundleEntry(r.value[0], i) });
      } else {
        out.set(b.key, {
          status: "error",
          message: bundleLiveErrorText(r.status === "rejected" ? r.reason : "")
        });
      }
    });
    return out;
  }
}

export function useBundleCartPricing(
  bundles: readonly CartBundle[],
  customerId: string | null,
  /** Slot grup pembeli — harga level berubah → hitung ulang. */
  groupSlot: number | null = null
): BundleCartPricing {
  const signature = useMemo(() => bundleCartSignature(bundles), [bundles]);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!signature || !customerId) {
      setAnswer(null);
      return;
    }
    let alive = true;
    const list = bundles;
    const sig = signature;
    const owner = `${customerId}#${groupSlot ?? ""}`;
    const gen = tick;
    const id = setTimeout(() => {
      void resolveLive(list, customerId).then((byKey) => {
        if (alive) setAnswer({ sig, owner, tick: gen, byKey });
      });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(id);
    };
    // `signature` merangkum isi `bundles`; groupSlot & tick memaksa hitung ulang.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, customerId, groupSlot, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  const owner = customerId ? `${customerId}#${groupSlot ?? ""}` : null;
  return useMemo(() => {
    // Jawaban milik pembeli/level lain tak pernah dipajang.
    const mine = answer !== null && owner !== null && answer.owner === owner && Boolean(signature) ? answer : null;
    const lastByKey = mine ? mine.byKey : null;
    // Galat ditempel per kunci bundel — jawaban terakhir cukup untuk menahan
    // checkout (checkout sendiri memeriksa ulang semuanya).
    const hasError = Boolean(lastByKey) && bundles.some((b) => lastByKey?.get(b.key)?.status === "error");
    if (!mine || mine.sig !== signature || mine.tick !== tick) {
      return { signature, ready: false, byKey: null, lastByKey, entries: null, hasError, reload };
    }
    const entries: ResolvedBundle[] = [];
    for (const b of bundles) {
      const live = mine.byKey.get(b.key);
      if (live?.status === "ok") entries.push(live.entry);
    }
    return { signature, ready: true, byKey: mine.byKey, lastByKey, entries, hasError, reload };
  }, [answer, owner, bundles, signature, tick, reload]);
}
